/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";
import {
  BiSolidBot,
  BiSolidBulb,
  BiSolidConversation,
  BiSolidMoviePlay,
  BiSolidPackage,
  BiSolidUserPlus
} from "react-icons/bi";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useGetBotsQuery } from "../../app/services/chatbot-rtk.services";
import { store } from "../../app/store";
import AssistantIcon from "../../components/common/icons/assistant.icon";
import LoadingComponent from "../../components/common/loading/loading.component";
import ProBadge from "../../components/ui/ProBadge";
import usePermission from "../../hooks/usePermission";
import { BotCategoryEnum } from "../../utils/BotCategoryEnum";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { setChatbotTitle } from "../knowledge.slice";
import {
  handleInitializeWizard,
  handleModifyBotName,
  versionSelectors,
  wizardState
} from "./botSlice";
import {
  AIKittyType,
  BotTemplates,
  BotVersion,
  CloneBotTemplate
} from "./types";

const BotEngineSelector = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const permissions = usePermission();

  const { reset } = useFormContext();

  const versions = versionSelectors.selectAll(store.getState());
  const wizard = useAppSelector(wizardState);

  const { isLoading, isError, isSuccess, refetch } = useGetBotsQuery();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const inputSchema = Yup.string()
    .required()
    .min(1)
    .max(30, t("Wizard/validationLabels/key16"))
    .label(t("Wizard/step1/key7").toString());

  const [validationErrors, setValidationErrors] = useState<{
    bot_name: string[];
  }>();

  const handleSelectWidget = useCallback(
    (vers: BotVersion) => {
      if (!wizard.isInEditMode) {
        reset();

        dispatch(
          handleInitializeWizard({
            bot_code: vers.code,
            bot_type: vers.type,
            title: inputRef.current?.value
          })
        );
      }
    },
    [wizard.isInEditMode]
  );

  const handleSelectCloneTemplate = useCallback(
    (vers: BotVersion, template: BotTemplates) => {
      if (!wizard.isInEditMode) {
        reset();

        dispatch(
          handleInitializeWizard({
            bot_code: vers.code,
            bot_type: vers.type,
            title: inputRef.current?.value,
            cloneExtra: {
              template: template.id as CloneBotTemplate,
              data:
                (template.id as CloneBotTemplate) !== "basic"
                  ? {
                      botId:
                        (template.id as CloneBotTemplate) === "clonePlusVideo"
                          ? ""
                          : undefined,
                      name:
                        (template.id as CloneBotTemplate) ===
                        "clonePlusDescription"
                          ? ""
                          : undefined,
                      profession:
                        (template.id as CloneBotTemplate) ===
                        "clonePlusDescription"
                          ? ""
                          : undefined,
                      bio:
                        (template.id as CloneBotTemplate) ===
                        "clonePlusDescription"
                          ? ""
                          : undefined
                    }
                  : undefined,
              externalLinks:
                (template.id as CloneBotTemplate) !== "basic" ? [] : []
            }
          })
        );
      }
    },
    [wizard.isInEditMode]
  );

  const handleChooseNameForBot = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(handleModifyBotName(e.target.value));
    dispatch(setChatbotTitle(e.target.value));

    inputSchema
      .validate(e.target.value, {})
      .then(() => {
        setValidationErrors(undefined);

        inputRef.current?.classList.remove("tw-border-red-600");
      })
      .catch((err) => {
        inputRef.current?.classList.add("tw-border-red-600");

        setValidationErrors({ bot_name: err.errors });
      });
  };

  const hasRequiredPermission = (code: string) => {
    return wizard.category === "widget"
      ? permissions.widgetPage.main.some((dr) => dr.includes(code))
      : !permissions?.homePage?.extension?.upgrade;
  };

  const hasRequiredTemplatePermission = (code: string) => {
    // TODO => Should handle permission if available
    return true;
  };

  useEffect(() => {
    if (wizard.bot_code) {
      if (wizard.bot_config?.title) {
        dispatch(setChatbotTitle(wizard.bot_config?.title));
      }
    }
  }, [wizard.bot_code, wizard.bot_config?.title]);

  useEffect(() => {
    inputRef.current?.focus();
    // Cleanup logic when the component unmounts or when a specific dependency changes
    return () => {
      inputRef.current = null;
    };
  }, []);

  const iconsAndText: {
    [key: string]: {
      icon: React.ReactNode;
      title: string;
      description: React.ReactNode;
    };
  } = {
    MCBOT_01: {
      icon: <BiSolidConversation className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key3"),
      description: (
        <div className="tw-mt-2">
          <span className="tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {ReactHtmlParser(t("DashboardCreatePartial/MCBOT_01/key1"))}
            <br />
          </span>
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {" "}
              {ReactHtmlParser(t("DashboardCreatePartial/MCBOT_01/key2"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/MCBOT_01/key3"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/MCBOT_01/key4"))}
            </span>
          </div>
        </div>
      )
    },
    VIDEO_SCENARIO: {
      icon: <BiSolidMoviePlay className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key5"),
      description: (
        <div className="tw-mt-2">
          <span className="tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {ReactHtmlParser(t("DashboardCreatePartial/VIDEO_SCENARIO/key1"))}
            <br />
          </span>
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {" "}
              {ReactHtmlParser(t("DashboardCreatePartial/VIDEO_SCENARIO/key2"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/VIDEO_SCENARIO/key3"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/VIDEO_SCENARIO/key4"))}
            </span>
          </div>
        </div>
      )
    },
    KITTY_02: {
      icon: <BiSolidBot className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key4"),
      description: (
        <div className="tw-mt-2">
          <span className="tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {ReactHtmlParser(t("DashboardCreatePartial/KITTY_02/key1"))}
            <br />
          </span>
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_02/key2"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_02/key3"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_02/key4"))}
            </span>
          </div>
        </div>
      )
    },
    KITTY_03: {
      icon: <BiSolidBulb className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key2"),
      description: (
        <div className="tw-mt-2">
          <span className="tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {ReactHtmlParser(t("DashboardCreatePartial/KITTY_03/key1"))}
            <br />
          </span>
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_03/key2"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_03/key3"))}
              <br />{" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_03/key4"))}
            </span>
          </div>
        </div>
      )
    },
    CLONE_01: {
      icon: <BiSolidUserPlus className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key1"),
      description: null
    },
    KITTY_04: {
      icon: <BiSolidPackage className="tw-text-blue-500" size={25} />,
      title: t("DashboardCreatePartial/key6"),
      description: (
        <div className="tw-mt-2">
          <span className="tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            {ReactHtmlParser(t("DashboardCreatePartial/KITTY_04/key1"))}
            <br />
          </span>
          <div className="tw-mt-10">
            <span className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
              {" "}
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_04/key2"))}
              <br />
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_04/key3"))}
              <br />
              {ReactHtmlParser(t("DashboardCreatePartial/KITTY_04/key4"))}
            </span>
          </div>
        </div>
      )
    },
    VIDEO_AI: {
      icon: <AssistantIcon />,
      title: t("DashboardCreatePartial/key7"),
      description: null
    },
    PHONE_HOTLINE: {
      icon: <AssistantIcon />,
      title: t("DashboardCreatePartial/key8"),
      description: null
    }
  };

  const videoMapper: { [key: string]: string } = {
    KITTY_02:
      "https://cdn.1cdn.app/application/LOUISEBOT/24062408291855_AI-bot_(2).mp4",
    KITTY_03:
      "https://cdn.1cdn.app/application/LOUISEBOT/2406240829186_AI-knowledge-bot.mp4",
    KITTY_04:
      "https://cdn.1cdn.app/application/LOUISEBOT/24030408595450_Assistant_with_AI.mp4",
    MCBOT_01:
      "https://cdn.1cdn.app/application/LOUISEBOT/2406240829184_text-bot.mp4",
    VIDEO_SCENARIO:
      "https://cdn.1cdn.app/application/LOUISEBOT/24062408291812_video-bot.mp4",
    CLONE_01:
      "https://cdn.1cdn.app/application/LOUISEBOT/24062408291855_AI-bot_(2).mp4"
  };

  let content;

  if (isLoading) {
    content = (
      <div className="tw-px-8">
        <LoadingComponent />
      </div>
    );
  }

  if (isError) {
    content = (
      <div>
        {t("Wizard/general/key10")}
        <Button variant="link" onClick={refetch}>
          {t("Wizard/general/key11")}
        </Button>
      </div>
    );
  }

  if (isSuccess) {
    content = (
      <div className="tw-grid tw-grid-cols-10 tw-w-full xl:tw-gap-2 tw-gap-4">
        {[
          ...(wizard.category === "widget"
            ? versions.filter(
                (item) =>
                  item.code !== "KITTY_01" &&
                  item.code !== BotTypeEnum.HUMAN_CLONE
              )
            : versions.filter((item) => item.code === BotTypeEnum.HUMAN_CLONE))
        ].map((vers) => {
          if (!vers.has_templates)
            return (
              <div
                key={vers.code}
                className={`xl:tw-col-span-2 sm:tw-col-span-5 tw-col-span-10 tw-flex tw-flex-col ${
                  wizard.isInEditMode || !hasRequiredPermission(vers.code)
                    ? "tw-cursor-not-allowed"
                    : ""
                } ${
                  wizard.bot_code === vers.code
                    ? "tw-border-4 tw-border-blue-500"
                    : "tw-border-4 tw-border-transparent"
                } ${
                  wizard.isInEditMode && wizard.bot_code !== vers.code
                    ? "tw-hidden"
                    : "tw-inline-flex"
                } tw-flex-1 tw-relative tw-border-solid tw-rounded-xl`}
                onClick={() =>
                  hasRequiredPermission(vers.code) && handleSelectWidget(vers)
                }
                onKeyDown={() =>
                  hasRequiredPermission(vers.code) && handleSelectWidget(vers)
                }
                role="button"
                aria-label="bot-selector"
                tabIndex={0}
              >
                {(wizard.isInEditMode && wizard.bot_code !== vers.code) ||
                  (!hasRequiredPermission(vers.code) && (
                    <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-bg-gray-200 tw-bg-opacity-50" />
                  ))}
                <div className="tw-self-stretch tw-bg-white tw-rounded-tl-lg tw-rounded-tr-lg tw-h-[147.95px]">
                  <video
                    playsInline
                    autoPlay
                    loop
                    muted
                    preload="metadata"
                    className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-aspect-video"
                  >
                    <source
                      src={`${videoMapper[vers.code]}#t=0.001`}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="tw-self-stretch tw-flex-1 tw-px-6 tw-pt-4 tw-pb-8 tw-bg-white tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
                  <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-inline-flex tw-gap-3 tw-mb-3">
                    <div className="tw-flex tw-items-center">
                      <div className="tw-p-3 tw-bg-blue-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-relative">
                        <div className="tw-w-6 tw-h-6 tw-relative tw-justify-center tw-items-center tw-flex">
                          {iconsAndText[vers.code].icon}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`tw-px-3 tw-py-0.5 ${
                        vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                          ? "tw-bg-emerald-100"
                          : "tw-bg-gray-100"
                      }  tw-rounded-xl tw-justify-center tw-items-center tw-gap-1.5 tw-flex`}
                    >
                      <div
                        className={`tw-text-center ${
                          vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                            ? "tw-text-emerald-800"
                            : "tw-text-gray-600 "
                        } tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight`}
                      >
                        {vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                          ? t("Wizard/step1/key1")
                          : t("Wizard/step1/key2")}
                      </div>
                    </div>
                  </div>
                  {!hasRequiredPermission(vers.code) && (
                    <ProBadge className="tw-w-4 tw-h-4 tw-p-0.5  tw-rounded-[20px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-absolute tw-right-0 tw-bottom-0" />
                  )}
                  <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-1 tw-flex">
                    <div className="tw-self-stretch tw-text-gray-900 text-base font-normal tw-font-['Lexend'] tw-leading-normal">
                      {iconsAndText[vers.code].title}
                    </div>
                  </div>

                  {iconsAndText[vers.code].description}
                </div>
              </div>
            );
          else
            return (
              <React.Fragment key={vers.code}>
                {vers.templates?.map((template) => (
                  <div
                    key={template.id}
                    className={`xl:tw-col-span-2 sm:tw-col-span-5 tw-col-span-10 tw-flex tw-flex-col ${
                      wizard.isInEditMode ||
                      !hasRequiredTemplatePermission(template.id as string)
                        ? "tw-cursor-not-allowed"
                        : ""
                    } ${
                      (wizard.bot_config as AIKittyType).cloneExtra
                        ?.template === (template.id as string)
                        ? "tw-border-4 tw-border-blue-500"
                        : "tw-border-4 tw-border-transparent"
                    } ${
                      wizard.isInEditMode &&
                      (wizard.bot_config as AIKittyType).cloneExtra
                        ?.template !== (template.id as string)
                        ? "tw-hidden"
                        : "tw-inline-flex"
                    } tw-flex-1 tw-relative tw-border-solid tw-rounded-xl`}
                    onClick={() =>
                      hasRequiredTemplatePermission(template.id as string) &&
                      handleSelectCloneTemplate(vers, template)
                    }
                    onKeyDown={() =>
                      hasRequiredTemplatePermission(template.id as string) &&
                      handleSelectCloneTemplate(vers, template)
                    }
                    role="button"
                    aria-label="bot-selector"
                    tabIndex={0}
                  >
                    {(wizard.isInEditMode &&
                      (wizard.bot_config as AIKittyType).cloneExtra
                        ?.template === (template.id as string)) ||
                      (!hasRequiredTemplatePermission(
                        template.id as string
                      ) && (
                        <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-bg-gray-200 tw-bg-opacity-50" />
                      ))}
                    <div className="tw-self-stretch tw-bg-white tw-rounded-tl-lg tw-rounded-tr-lg tw-h-[147.95px]">
                      {template.image ? (
                        <img
                          src={template.image}
                          className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-aspect-video"
                        />
                      ) : (
                        <video
                          playsInline
                          autoPlay
                          loop
                          muted
                          preload="metadata"
                          className="tw-w-full tw-h-full tw-object-cover tw-rounded-lg tw-aspect-video"
                        >
                          <source
                            src={`${videoMapper[vers.code]}#t=0.001`}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                    <div className="tw-self-stretch tw-flex-1 tw-px-6 tw-pt-4 tw-pb-8 tw-bg-white tw-rounded-bl-lg tw-rounded-br-lg tw-flex-col tw-justify-start tw-items-start tw-gap-3 flex">
                      {template.icon && (
                        <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-inline-flex tw-gap-3 tw-mb-3">
                          <div className="tw-flex tw-items-center">
                            <div className="tw-p-3 tw-bg-blue-100 tw-rounded-[99px] tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-relative">
                              <div className="tw-w-6 tw-h-6 tw-relative tw-justify-center tw-items-center tw-flex">
                                {template.icon}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
                        <div className="tw-self-stretch tw-w-full tw-justify-between tw-items-center tw-flex tw-flex-wrap tw-gap-3">
                          <div className="tw-self-stretch tw-text-gray-900 text-base font-normal tw-font-['Lexend'] tw-leading-normal">
                            {i18n.language.startsWith("en")
                              ? template.title.en
                              : template.title.fr}
                          </div>
                        </div>
                        {!hasRequiredTemplatePermission(
                          template.id as string
                        ) && (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="tooltip">
                                {t("Wizard/general/key12")}
                              </Tooltip>
                            }
                          >
                            <ProBadge className="tw-w-4 tw-h-4 tw-p-0.5  tw-rounded-[20px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-absolute tw-right-0 tw-bottom-0" />
                          </OverlayTrigger>
                        )}
                        <div
                          className={`tw-px-3 tw-py-0.5 ${
                            vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                              ? "tw-bg-emerald-100"
                              : "tw-bg-gray-100"
                          }  tw-rounded-xl tw-justify-center tw-items-center tw-gap-1.5 tw-flex`}
                        >
                          <div
                            className={`tw-text-center ${
                              vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                                ? "tw-text-emerald-800"
                                : "tw-text-gray-600 "
                            } tw-text-sm tw-font-medium tw-font-['Inter'] tw-leading-tight`}
                          >
                            {vers.type === BotCategoryEnum.AI_BASED_CHATBOT
                              ? t("Wizard/step1/key1")
                              : t("Wizard/step1/key2")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            );
        })}
      </div>
    );
  }

  return (
    <div className="bot-engine-selector md:tw-px-8 tw-px-1">
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex tw-mb-5">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {wizard.bot_code === BotTypeEnum.HUMAN_CLONE
              ? t("Wizard/step1/key10")
              : t("Wizard/step1/key7")}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {wizard.bot_code === BotTypeEnum.HUMAN_CLONE
              ? t("Wizard/step1/key11")
              : t("Wizard/step1/key8")}
          </div>
        </div>
        <div className="md:tw-max-w-[357px] tw-w-full tw-justify-start tw-items-start tw-gap-2 tw-flex tw-flex-col">
          <input
            maxLength={31}
            max={31}
            type="text"
            name="bot_title"
            id="bot_title"
            className={`tw-w-full tw-px-4 tw-py-2.5 tw-bg-white
            tw-rounded-md tw-shadow tw-border tw-border-gray-300 placeholder:tw-text-gray-400 tw-text-sm tw-font-normal tw-tw-font-['Lexend'] tw-leading-tight`}
            ref={inputRef}
            defaultValue={wizard.bot_config?.title}
            onChange={handleChooseNameForBot}
            inputMode="text"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            // disabled={!wizard.bot_code}
          />
          {!!validationErrors?.bot_name && (
            <p className="tw-text-red-600 tw-text-xs">
              {validationErrors.bot_name.map((er) => `${er}\n`)}
            </p>
          )}
        </div>
      </div>

      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-5">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {wizard.category === "widget"
              ? t("Wizard/step1/key3")
              : t("Wizard/step1/key4")}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
            {wizard.category === "widget"
              ? t("Wizard/step1/key5")
              : t("Wizard/step1/key6")}
          </div>
        </div>

        {content}
      </div>
    </div>
  );
};

export default BotEngineSelector;
